import React, { useState, useEffect } from "react";
import ShortsVideoModal from "../components/ShortsVideoModal";
import "../styles/Pages.css";
import "../styles/HariParamrut.css";
import "../styles/Shorts.css";

const monthNameToNumber = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

const getMonthSortValue = (month) => {
  if (month === null || month === undefined) return 0;

  const monthText = String(month).trim();
  if (!monthText) return 0;

  const monthAsNumber = Number.parseInt(monthText, 10);
  if (!Number.isNaN(monthAsNumber)) return monthAsNumber;

  return monthNameToNumber[monthText.toLowerCase()] || 0;
};

const getYearSortValue = (year) => {
  const parsedYear = Number.parseInt(String(year), 10);
  return Number.isNaN(parsedYear) ? -Infinity : parsedYear;
};

const getDatePart = (dateValue, partIndex) => {
  if (!dateValue) return null;
  const parts = String(dateValue).split("-");
  if (parts.length !== 3) return null;
  return parts[partIndex] || null;
};

const getYouTubeVideoId = (url = "") => {
  try {
    const parsedUrl = new URL(url);
    const { hostname, pathname, searchParams } = parsedUrl;

    if (hostname.includes("youtu.be")) {
      return pathname.replace("/", "");
    }

    if (pathname.startsWith("/shorts/")) {
      return pathname.split("/shorts/")[1]?.split("/")[0] || "";
    }

    return searchParams.get("v") || "";
  } catch {
    return url.replace("https://www.youtube.com/watch?v=", "");
  }
};

function Shorts() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(-1);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          "https://hariprabodham.org.in/api/?category=18",
        );

        if (!response.ok) {
          throw new Error("Failed to fetch videos");
        }
        const data = await response.json();

        const transformedVideos = data.map((video) => {
          const yearFromDate = getDatePart(video.InfoDate, 2);
          const monthFromDate = getDatePart(video.InfoDate, 1);
          const infoyear =
            video.InfoYear ?? video.infoyear ?? yearFromDate ?? "Unknown";
          const infomonth =
            video.InfoMonth ?? video.infomonth ?? monthFromDate ?? null;

          return {
            id: getYouTubeVideoId(video.VideoUrl),
            title: video.Title,
            location: video.City || "",
            infoyear,
            infomonth,
          };
        });

        setVideos(transformedVideos);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching videos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const openVideoModal = (index) => {
    setSelectedVideoIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideoIndex(-1);
  };

  const showNextVideo = () => {
    setSelectedVideoIndex((prevIndex) =>
      prevIndex < videos.length - 1 ? prevIndex + 1 : prevIndex,
    );
  };

  const showPrevVideo = () => {
    setSelectedVideoIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : prevIndex,
    );
  };

  const videosByYear = videos.reduce((groups, video) => {
    const year = video.infoyear || "Unknown";
    if (!groups[year]) groups[year] = [];
    groups[year].push(video);
    return groups;
  }, {});

  const sortedYears = Object.keys(videosByYear).sort(
    (a, b) => getYearSortValue(b) - getYearSortValue(a),
  );

  const orderedVideos = [];
  const groupedVideos = sortedYears.map((year) => {
    const yearVideos = [...videosByYear[year]].sort(
      (a, b) => getMonthSortValue(b.infomonth) - getMonthSortValue(a.infomonth),
    );
    const videosWithIndexes = yearVideos.map((video) => {
      const orderedIndex = orderedVideos.length;
      orderedVideos.push(video);
      return {
        ...video,
        orderedIndex,
      };
    });

    return {
      year,
      videos: videosWithIndexes,
    };
  });

  const selectedVideo =
    selectedVideoIndex >= 0 ? orderedVideos[selectedVideoIndex] : null;

  return (
    <main className="page-content pravachan-page shorts-page">
      <section className="page-header">
        <h1 className="section-title">Shorts</h1>
        <p className="header-subtitle"></p>
      </section>

      <section className="page-body">
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading videos...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p>Error loading videos: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {groupedVideos.map(({ year, videos: yearVideos }, index) => (
              <section
                key={year}
                style={index === 0 ? undefined : { marginTop: "28px" }}
              >
                <h2 style={{ marginBottom: "12px" }}>{year}</h2>
                <div className="video-grid shorts-grid">
                  {yearVideos.map((video) => (
                    <div
                      key={`${video.id}-${video.title}`}
                      className="video-card shorts-card"
                      onClick={() => openVideoModal(video.orderedIndex)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter")
                          openVideoModal(video.orderedIndex);
                      }}
                    >
                      <div className="video-thumbnail shorts-thumbnail">
                        <img
                          src={`https://i.ytimg.com/vi/${video.id}/oar2.jpg`}
                          alt={video.title}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                          }}
                        />
                        <div className="play-overlay">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="video-info">
                        <h3>{video.title}</h3>
                        {video.location && (
                          <p className="video-location">{video.location}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </section>

      {isModalOpen && (
        <ShortsVideoModal
          videoId={selectedVideo?.id}
          title={selectedVideo?.title}
          isOpen={isModalOpen}
          onClose={closeModal}
          onNext={showNextVideo}
          onPrev={showPrevVideo}
          canGoNext={selectedVideoIndex < orderedVideos.length - 1}
          canGoPrev={selectedVideoIndex > 0}
        />
      )}
    </main>
  );
}

export default Shorts;
